/**
 * FetchProgress — TeamGrid analyzing state while Gmail metadata is fetched.
 */

import React, { useEffect, useState } from 'react';
import { Typography } from 'antd';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Mail, Search, Share2, Sparkles } from 'lucide-react';

const { Text } = Typography;

const PHASES = [
  { icon: Mail, label: 'Collecting mailbox metadata' },
  { icon: Search, label: 'Reading headers & timestamps' },
  { icon: Share2, label: 'Mapping threads and contacts' },
  { icon: Sparkles, label: 'TeamGrid is analyzing patterns' },
];

export function FetchProgress({ fetched, total, isDark }) {
  const reduced = useReducedMotion();
  const pct = total > 0 ? Math.min(100, Math.round((fetched / total) * 100)) : 0;
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (total > 0) {
      if (pct < 25) setPhase(0);
      else if (pct < 55) setPhase(1);
      else if (pct < 85) setPhase(2);
      else setPhase(3);
      return undefined;
    }
    if (reduced) return undefined;
    const id = setInterval(() => {
      setPhase((p) => (p + 1) % PHASES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [reduced, pct, total]);

  const ink = isDark ? '#f8fafc' : '#0f172a';
  const mute = isDark ? '#94a3b8' : '#64748b';
  const card = isDark ? 'rgba(255,255,255,0.035)' : '#ffffff';
  const line = isDark ? 'rgba(255,255,255,0.07)' : '#eceef3';
  const PhaseIcon = PHASES[phase].icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.35 }}
      style={{ display: 'flex', justifyContent: 'center', padding: '36px 12px 20px' }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          textAlign: 'center',
          background: card,
          border: `1px solid ${line}`,
          borderRadius: 22,
          padding: '42px 32px 30px',
          boxShadow: isDark
            ? '0 16px 44px rgba(0,0,0,0.28)'
            : '0 14px 40px rgba(99,102,241,0.08)',
        }}
      >
        <div style={{ position: 'relative', width: 96, height: 96, margin: '0 auto 22px' }}>
          {!reduced && [0, 1].map((i) => (
            <motion.div
              key={i}
              animate={{ scale: [1, 1.55], opacity: [0.4, 0] }}
              transition={{ duration: 2.1, repeat: Infinity, ease: 'easeOut', delay: i * 0.7 }}
              style={{
                position: 'absolute',
                inset: 8,
                borderRadius: '50%',
                border: '1.5px solid rgba(99,102,241,0.4)',
              }}
            />
          ))}
          <motion.div
            animate={reduced ? {} : { rotate: 360 }}
            transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
            style={{
              position: 'absolute', inset: 0,
              borderRadius: '50%',
              border: '1.5px dashed rgba(99,102,241,0.28)',
            }}
          />
          <motion.div
            animate={reduced ? {} : { scale: [1, 1.04, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              inset: 18,
              borderRadius: 22,
              background: 'linear-gradient(145deg, #6366f1 0%, #7c3aed 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 26px rgba(99,102,241,0.36)',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={phase}
                initial={{ opacity: 0, scale: 0.7 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.7 }}
                transition={{ duration: 0.22 }}
              >
                <PhaseIcon size={26} color="#fff" strokeWidth={1.8} />
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>

        <Text strong style={{ display: 'block', fontSize: 18, color: ink, letterSpacing: '-0.3px', marginBottom: 6 }}>
          TeamGrid is analyzing
        </Text>

        <div style={{ minHeight: 22, marginBottom: 6 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={PHASES[phase].label}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.22 }}
            >
              <Text style={{ fontSize: 13.5, color: mute }}>{PHASES[phase].label}</Text>
            </motion.div>
          </AnimatePresence>
        </div>

        <Text style={{ fontSize: 12.5, color: mute }}>
          {total > 0
            ? `${fetched.toLocaleString()} of ${total.toLocaleString()} messages`
            : 'Counting messages in Inbox and Sent…'}
        </Text>

        <div style={{ marginTop: 22 }}>
          <div
            style={{
              height: 7,
              borderRadius: 99,
              background: isDark ? 'rgba(255,255,255,0.07)' : '#eceef5',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <motion.div
              animate={{ width: `${total > 0 ? pct : 18}%` }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              style={{
                height: '100%',
                borderRadius: 99,
                background: 'linear-gradient(90deg, #6366f1, #8b5cf6)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {!reduced && (
                <motion.div
                  animate={{ x: ['-40%', '220%'] }}
                  transition={{ duration: 1.3, repeat: Infinity, ease: 'linear' }}
                  style={{
                    position: 'absolute', inset: 0,
                    width: '40%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                  }}
                />
              )}
            </motion.div>
          </div>
          <Text style={{ display: 'block', marginTop: 8, fontSize: 11.5, color: mute }}>
            {total > 0 ? `${pct}% complete` : 'Preparing analysis'}
            {total > 1000 ? ' · large mailbox — this can take a few minutes' : ''}
          </Text>
        </div>
      </div>
    </motion.div>
  );
}
